import * as THREE from 'three/webgpu';
import { fog, rangeFogFactor, uniform, positionWorld, cameraPosition, dot, max, mix, pow, float, normalize } from 'three/tsl';
import { SkyMesh } from 'three/addons/objects/SkyMesh.js';
import { CSMShadowNode } from 'three/addons/csm/CSMShadowNode.js';
import type { BuiltStage, MeshData, PropInstance } from '../world/types';
import type { SurfaceId } from '../sim/types';
import type { GameTextures } from './textures';
import { terrainMaterial, roadMaterial, waterMaterial, damageDecalMaterial } from './ground';
import { FoliageSystem, conifer, rockGeometry, fernGeometry, foliageMaterial, rockMaterial, makeBillboard, bakeBillboards, type Billboard } from './foliage';
import { batchFlat } from './batch';
import { GrassField } from './grass';
import { VehicleEffects } from './effects';
import { Crowds } from './spectators';
import type { QualitySettings } from './quality';
import { batchByMaterial } from './batch';

const SURFACE_COLORS: Record<SurfaceId, number> = {
  tarmac: 0x3a3b3d,
  concrete: 0x8a8a86,
  gravel: 0x9c8a70,
  loose: 0xb39f80,
  mud: 0x4f3e2c,
  wet: 0x6e5d47,
  water: 0x4a5a5e,
  grass: 0x5b7034,
  rock: 0x77746e,
};

export function meshFromData(d: MeshData): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(d.positions, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(d.normals, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(d.uvs, 2));
  if (d.colors) g.setAttribute('color', new THREE.BufferAttribute(d.colors, 3));
  if (d.splat) g.setAttribute('splat', new THREE.BufferAttribute(d.splat, 3));
  g.setIndex(new THREE.BufferAttribute(d.indices, 1));
  g.computeBoundingSphere();
  g.computeBoundingBox();
  return g;
}

/** Concatenate mesh chunks (same attribute layout) into one — fewer draw calls. */
function mergeData(parts: MeshData[]): MeshData {
  let nv = 0;
  let ni = 0;
  for (const p of parts) {
    nv += p.positions.length / 3;
    ni += p.indices.length;
  }
  const positions = new Float32Array(nv * 3);
  const normals = new Float32Array(nv * 3);
  const uvs = new Float32Array(nv * 2);
  const colors = parts[0].colors ? new Float32Array(nv * 3) : undefined;
  const splat = parts[0].splat ? new Float32Array(nv * 3) : undefined;
  const indices = new Uint32Array(ni);
  let ov = 0;
  let oi = 0;
  for (const p of parts) {
    const n = p.positions.length / 3;
    positions.set(p.positions, ov * 3);
    normals.set(p.normals, ov * 3);
    uvs.set(p.uvs, ov * 2);
    if (colors && p.colors) colors.set(p.colors, ov * 3);
    if (splat && p.splat) splat.set(p.splat, ov * 3);
    for (let i = 0; i < p.indices.length; i++) indices[oi + i] = p.indices[i] + ov;
    ov += n;
    oi += p.indices.length;
  }
  return { positions, normals, uvs, indices, colors, splat };
}

/** Sky, sun, terrain, road, vegetation, water, effects and stage furniture for a built stage. */
export class WorldView {
  readonly scene = new THREE.Scene();
  readonly sun = new THREE.DirectionalLight(0xfff0d6, 4.2);
  readonly hemi = new THREE.HemisphereLight(0xaac3e6, 0x5a4a35, 0.16);
  readonly sky: SkyMesh;
  readonly sunDir = new THREE.Vector3();
  readonly foliage = new FoliageSystem();
  readonly grass: GrassField | null = null;
  readonly effects = new VehicleEffects();
  /** Trackside spectators (road stages with hotspots). */
  crowds: Crowds | null = null;
  /** Scene light level for unlit-shaded particles (dust is lit by sun + sky). */
  readonly lightLevel = new THREE.Color(1, 1, 1);
  private csm: CSMShadowNode | null = null;
  /** With three or more cascades, the outer ones take turns (see `update`). */
  private cascadeTurn = 0;
  private readonly lastCameraPos = new THREE.Vector3(1e9, 1e9, 1e9);
  private billboards: Billboard[] = [];
  private treeMaterial: THREE.Material | null = null;
  private quality: QualitySettings;
  private fogBase: [number, number] = [140, 1500];
  private readonly fogNear = uniform(140);
  private readonly fogFar = uniform(1500);

  constructor(stage: BuiltStage, tx: GameTextures | null, quality: QualitySettings) {
    this.quality = quality;
    const env = stage.spec.environment;
    const phi = THREE.MathUtils.degToRad(90 - env.sunElevationDeg);
    const theta = THREE.MathUtils.degToRad(env.sunAzimuthDeg);
    this.sunDir.setFromSphericalCoords(1, phi, theta);

    this.sky = new SkyMesh();
    this.sky.scale.setScalar(4500);
    this.sky.turbidity.value = env.turbidity;
    this.sky.rayleigh.value = env.rayleigh;
    this.sky.mieCoefficient.value = 0.004;
    this.sky.mieDirectionalG.value = 0.82;
    this.sky.sunPosition.value.copy(this.sunDir);
    this.scene.add(this.sky);

    this.sun.position.copy(this.sunDir).multiplyScalar(300);
    this.sun.castShadow = true;
    this.sun.shadow.bias = -0.0004;
    this.sun.shadow.normalBias = 0.03;
    this.scene.add(this.sun);
    this.scene.add(this.sun.target);
    this.scene.add(this.hemi);

    // Distance fog tinted toward the sun (aerial perspective); the sky itself stays unfogged.
    this.fogBase = [env.fogNear, env.fogFar];
    this.fogNear.value = env.fogNear;
    this.fogFar.value = env.fogFar;
    const fc = new THREE.Color(env.fogColor[0], env.fogColor[1], env.fogColor[2]);
    const haze = uniform(fc);
    const glowCol = uniform(new THREE.Color(1.0, 0.9, 0.74));
    const sunU = uniform(this.sunDir.clone());
    const view = normalize(positionWorld.sub(cameraPosition));
    const glow = pow(max(dot(view, sunU), float(0)), float(8));
    const factor = rangeFogFactor(this.fogNear, this.fogFar).mul(0.92);
    this.scene.fogNode = fog(mix(haze, glowCol, glow.mul(0.45)), factor);

    const sunAmt = Math.max(0, this.sunDir.y);
    this.lightLevel.setRGB(0.55 + 0.6 * sunAmt, 0.55 + 0.58 * sunAmt, 0.58 + 0.52 * sunAmt);

    if (stage.spec.kind === 'pad' || !tx) this.buildPad(stage);
    else this.grass = this.buildRoad(stage, tx);
    this.scene.add(this.effects.group);
    this.applyQuality(quality);
    normalizeShadowCasters(this.scene);
    // Everything built so far is static: compose matrices once instead of every frame.
    this.scene.updateMatrixWorld(true);
    // Plain scenery meshes (terrain, road, water, batched props) never change after the
    // environment is set: `static` lets three skip its per-frame material/geometry change check
    // for them (CPU and garbage per draw). Not the start lights (their materials change) or
    // anything instanced, particles, grass, foliage or the crowds.
    const dynamic = new Set<THREE.Object3D>([this.effects.group, this.foliage.group]);
    if (this.grass) dynamic.add(this.grass.mesh);
    if (this.crowds) dynamic.add(this.crowds.group);
    for (const o of this.scene.children) {
      if (o === this.sun || o === this.sun.target || o === this.sky) continue;
      const scenery = !dynamic.has(o);
      o.traverse((c) => {
        c.matrixAutoUpdate = false;
        c.matrixWorldAutoUpdate = false;
        if (scenery && (c as THREE.Mesh).isMesh && !(c as THREE.InstancedMesh).isInstancedMesh && !insideNamed(c, 'countdown-lights')) c.static = true;
      });
    }
  }

  private buildPad(stage: BuiltStage): void {
    const sampleSurface = (x: number, z: number) => stage.surfaces.surfaceAt(x, z);
    const mat = new THREE.MeshStandardNodeMaterial({ vertexColors: true, roughness: 0.95 });
    for (const t of [...stage.terrain, ...stage.road]) {
      const m = new THREE.Mesh(meshFromData(colorize(t, sampleSurface)), mat);
      m.receiveShadow = true;
      m.castShadow = true;
      this.scene.add(m);
    }
    this.scene.add(this.foliage.group);
  }

  private buildRoad(stage: BuiltStage, tx: GameTextures): GrassField {
    // Terrain: merge 160 m chunks into ~1280 m regions (≈20 draws instead of ~900).
    const tMat = terrainMaterial(tx);
    const regions = new Map<string, MeshData[]>();
    for (const t of stage.terrain) {
      const k = `${Math.floor(t.positions[0] / 1280)},${Math.floor(t.positions[2] / 1280)}`;
      let r = regions.get(k);
      if (!r) regions.set(k, (r = []));
      r.push(t);
    }
    for (const parts of regions.values()) {
      const m = new THREE.Mesh(meshFromData(mergeData(parts)), tMat);
      m.receiveShadow = true;
      this.scene.add(m);
    }
    const rMat = roadMaterial(tx);
    rMat.polygonOffset = true;
    rMat.polygonOffsetFactor = -1;
    rMat.polygonOffsetUnits = -1;
    // Road ribbon: four 100 m chunks per mesh (fewer draws, still frustum-culled in pieces).
    for (let i = 0; i < stage.road.length; i += 4) {
      const m = new THREE.Mesh(meshFromData(mergeData(stage.road.slice(i, i + 4))), rMat);
      m.receiveShadow = true;
      this.scene.add(m);
    }
    const wMat = waterMaterial();
    for (const w of stage.water ?? []) {
      const m = new THREE.Mesh(meshFromData(w), wMat);
      m.receiveShadow = true;
      this.scene.add(m);
    }
    if (stage.decals?.length) {
      const dMat = damageDecalMaterial();
      for (const d of stage.decals) {
        const m = new THREE.Mesh(meshFromData(d), dMat);
        m.receiveShadow = true;
        m.renderOrder = 1;
        m.name = 'road-damage';
        this.scene.add(m);
      }
    }
    // Trees, rocks, ferns. Near-LOD buffers are sized for the worst camera position along the road.
    const tr = stage.track;
    if (tr) for (let i = 0; i < tr.s.length; i += 40) this.foliage.probes.push({ x: tr.x[i], z: tr.z[i] });
    // No wind sway on shadow casters: three r186's shadow pass borrows each caster's positionNode
    // into one shared override material, and alternating it rebuilds render objects mid-frame
    // (GPU "buffer used in submit while destroyed"). Grass and ferns (no shadows) keep their sway.
    const needles = foliageMaterial(tx.spruceBranch, 20, 0);
    // Trunk and branches share one material (the card atlas carries a bark strip): one draw per LOD.
    const treeMats = needles;
    const tint = (base: [number, number, number], spread: number) => (i: number, out: THREE.Color) => {
      const h = Math.sin(i * 12.9898) * 43758.5453;
      const f = h - Math.floor(h);
      const g = Math.sin(i * 78.233) * 12345.678;
      const f2 = g - Math.floor(g);
      out.setRGB(base[0] * (1 - spread + 2 * spread * f), base[1] * (1 - spread * 0.6 + 1.2 * spread * f2), base[2] * (1 - spread + 2 * spread * f));
    };
    this.treeMaterial = needles;
    const lodsFor = (shape: 'spruce' | 'pine' | 'young', d: [number, number, number]) => {
      const bb = makeBillboard(shape);
      this.billboards.push(bb);
      return [
        { geometry: conifer(shape, 0), maxDist: d[0], castShadow: true },
        { geometry: conifer(shape, 1), maxDist: d[1], castShadow: true },
        { geometry: bb.geometry, material: bb.material, maxDist: d[2], castShadow: false, static: true },
      ];
    };
    for (const set of stage.instances) {
      switch (set.kind) {
        case 'spruce':
          this.foliage.add(set, lodsFor('spruce', [65, 200, 1100]), treeMats, tint([0.92, 1.0, 0.9], 0.14), 0.05);
          break;
        case 'pine':
          this.foliage.add(set, lodsFor('pine', [65, 200, 1100]), treeMats, tint([1.0, 1.0, 0.84], 0.14), 0.05);
          break;
        case 'birch':
          this.foliage.add(set, lodsFor('young', [55, 170, 650]), treeMats, tint([0.98, 1.05, 0.9], 0.12), 0.06);
          break;
        case 'rock':
          this.foliage.add(set, [{ geometry: rockGeometry(0), maxDist: 90, castShadow: true }, { geometry: rockGeometry(1), maxDist: 450, castShadow: false }], rockMaterial(tx), tint([1, 1, 1], 0.12));
          break;
        case 'bush': {
          const fern = foliageMaterial(tx.fern, 1.2, 0.05);
          // Half green fern, half dry bracken (rust-brown), as along real forest roads.
          this.foliage.add(set, [{ geometry: fernGeometry(), maxDist: 70, castShadow: false }], fern, (i, out) => {
            const h = Math.sin(i * 43.17) * 9813.13;
            const f = h - Math.floor(h);
            if (f < 0.5) out.setRGB(1.25 + 0.2 * f, 0.78 + 0.1 * f, 0.42);
            else out.setRGB(0.92 + 0.1 * f, 1.0, 0.82);
          });
          break;
        }
      }
    }
    this.scene.add(this.foliage.group);
    const grass = new GrassField(stage, tx.grassClump);
    this.scene.add(grass.mesh);
    // Stage furniture, batched per material (dozens of posts, boards and bales → a few draws).
    const props = new THREE.Group();
    props.name = 'props';
    for (const p of stage.props ?? []) {
      const o = propMesh(p);
      if (o) props.add(o);
    }
    batchFlat(props, (o) => o.name === 'countdown-lights', () => true, true, 'props');
    batchByMaterial(props, (o) => o.name === 'countdown-lights', () => true);
    this.scene.add(props);
    const crowds = new Crowds(stage.props ?? [], stage.visualHeightAt ?? stage.heightAt);
    if (crowds.sites.length) {
      this.crowds = crowds;
      this.scene.add(crowds.group);
    }
    return grass;
  }

  /** Apply a graphics preset: shadows (cascades, size, distance), view distances, grass, particles. */
  private startLamps: THREE.MeshStandardNodeMaterial[] | null = null;
  private warmState: [THREE.Object3D, boolean, boolean, number, number][] = [];

  /**
   * Loading-screen warm-up: render with every object visible and unculled so three builds each
   * render object (main pass and every shadow cascade) now, instead of mid-stage when it first
   * comes into view (a CPU spike and long-lived garbage each). Instanced meshes are drawn at full
   * capacity: three's instancing setup differs for small and large counts, so a ping-pong copy
   * warmed at count 0 would be rebuilt on its first real refill. `false` restores the scene.
   * The state is forced around each warm-up render (`warmFrame`), not once: per-frame logic (the
   * crowds' distance cull, empty particle layers, cockpit-only parts, ping-pong swaps) would hide
   * things again before the frame is drawn, and they would then compile mid-stage.
   */
  warming = false;

  warmup(on: boolean): void {
    this.warming = on;
  }

  /** Around each render while warming: `true` before (show everything), `false` after (restore). */
  warmFrame(before: boolean): void {
    if (before) {
      this.scene.traverse((o) => {
        const im = o as THREE.InstancedMesh;
        // Empty ring buffers (tyre marks) draw one quad's worth during the warm-up, never zero indices.
        const dr = (o as THREE.Mesh).geometry?.drawRange;
        const emptyRange = dr && dr.count === 0;
        this.warmState.push([o, o.visible, o.frustumCulled, im.isInstancedMesh ? im.count : -1, emptyRange ? 0 : -1]);
        o.visible = true;
        o.frustumCulled = false;
        if (im.isInstancedMesh) im.count = im.instanceMatrix.count;
        if (emptyRange) dr.count = 6;
      });
    } else {
      for (const [o, v, f, c, r] of this.warmState) {
        o.visible = v;
        o.frustumCulled = f;
        if (c >= 0) (o as THREE.InstancedMesh).count = c;
        if (r >= 0) (o as THREE.Mesh).geometry.drawRange.count = r;
      }
      this.warmState = [];
    }
  }

  /** Start gantry: `lit` red lamps (0–5) or all green. */
  setStartLights(lit: number, green: boolean): void {
    if (!this.startLamps) {
      const g = this.scene.getObjectByName('countdown-lights');
      this.startLamps = [];
      g?.traverse((o) => {
        const m = (o as THREE.Mesh).material as THREE.MeshStandardNodeMaterial | undefined;
        if ((o as THREE.Mesh).isMesh && m) this.startLamps!.push(m);
      });
    }
    // Lamps are built right-to-left as seen from the grid: fill them left-to-right for the driver.
    const n = this.startLamps.length;
    this.startLamps.forEach((m, i) => {
      const on = green || n - 1 - i < lit;
      // Saturated primaries at moderate intensity so the tone mapper keeps them red/green.
      m.emissive.setHex(green ? 0x00ff22 : on ? 0xff0000 : 0x000000);
      m.emissiveIntensity = on ? 2.2 : 0;
      m.color.setHex(on ? 0x050505 : 0x220000);
    });
  }

  applyQuality(q: QualitySettings): void {
    this.quality = q;
    this.fogNear.value = this.fogBase[0] * q.fogScale;
    this.fogFar.value = this.fogBase[1] * q.fogScale;
    this.foliage.distanceScale = q.vegetation;
    this.foliage.shadows = q.shadowCascades > 0;
    if (this.grass) this.grass.radius = q.grassRadius;
    this.effects.amount = q.particles;
    this.sun.castShadow = q.shadowCascades > 0;
    this.sun.shadow.mapSize.set(q.shadowMapSize, q.shadowMapSize);
    if (this.csm) {
      this.csm.dispose();
      this.csm = null;
    }
    if (q.shadowCascades > 0) {
      const csm = new CSMShadowNode(this.sun, { cascades: q.shadowCascades, maxFar: q.shadowDistance, mode: 'practical', lightMargin: 160 });
      csm.fade = true;
      (this.sun.shadow as unknown as { shadowNode: unknown }).shadowNode = csm;
      this.csm = csm;
    }
    this.scene.traverse((o) => {
      const m = (o as THREE.Mesh).material;
      if (Array.isArray(m)) m.forEach((x) => (x.needsUpdate = true));
      else if (m) (m as THREE.Material).needsUpdate = true;
    });
  }

  /** Call after the camera's projection changes (resize, FOV). */
  cameraChanged(): void {
    this.csm?.updateFrustums();
  }

  /** Render the far-LOD tree billboards (needs the renderer; call once after construction). */
  bakeBillboards(renderer: THREE.WebGPURenderer): void {
    if (this.billboards.length && this.treeMaterial) bakeBillboards(renderer, this.billboards, this.treeMaterial);
  }

  /** Image-based lighting: prefilter the sky into an environment map (regenerate if the sun moves). */
  buildEnvironment(renderer: THREE.WebGPURenderer): void {
    const envScene = new THREE.Scene();
    const sky = new SkyMesh();
    sky.scale.setScalar(900);
    sky.turbidity.value = this.sky.turbidity.value;
    sky.rayleigh.value = this.sky.rayleigh.value;
    sky.mieCoefficient.value = this.sky.mieCoefficient.value;
    sky.mieDirectionalG.value = this.sky.mieDirectionalG.value;
    sky.sunPosition.value.copy(this.sunDir);
    envScene.add(sky);
    // Forest-green ground hemisphere so reflections aren't lit from below.
    const ground = new THREE.Mesh(new THREE.CircleGeometry(800, 32).rotateX(-Math.PI / 2), new THREE.MeshBasicNodeMaterial({ color: 0x1d2418 }));
    ground.position.y = -2;
    envScene.add(ground);
    const pmrem = new THREE.PMREMGenerator(renderer);
    const env = pmrem.fromScene(envScene, 0.02, 0.1, 2000).texture;
    this.scene.environment = env;
    this.scene.environmentIntensity = 0.45;
    pmrem.dispose();
    // Materials that take more or less sky than the rest get their own envMap (see setEnvScale).
    this.scene.traverse((o) => {
      const mats = (o as THREE.Mesh).material;
      if (!mats) return;
      for (const m of Array.isArray(mats) ? mats : [mats]) {
        const k = m.userData.envScale as number | undefined;
        if (typeof k !== 'number') continue;
        const sm = m as THREE.MeshStandardMaterial;
        sm.envMap = env;
        sm.envMapIntensity = this.scene.environmentIntensity * k;
        sm.needsUpdate = true;
      }
    });
  }

  /** Per-frame: sun/shadows follow the player; vegetation and grass follow the camera. */
  update(focus: THREE.Vector3, camera: THREE.Camera): void {
    // The sky box travels with the camera (the stage spans several km from the origin).
    this.sky.position.copy(camera.position);
    this.sun.target.position.copy(focus);
    this.sun.position.set(focus.x + this.sunDir.x * 300, focus.y + this.sunDir.y * 300, focus.z + this.sunDir.z * 300);
    this.foliage.update(camera.position.x, camera.position.z);
    this.grass?.update(camera.position.x, camera.position.z);
    this.crowds?.update(performance.now() / 1000, focus);
    this.turnCascades(camera);
  }

  /**
   * High and Ultra: the first cascade (the car and the road ahead) is drawn every frame and the
   * outer ones take turns, so a frame has two shadow passes instead of three. Each render pass leaves
   * a command encoder, a pass encoder and a command buffer (Blink objects) for the next scavenge,
   * plus its draws' uniform uploads and GPU time. A cascade that sits a frame out keeps its map and
   * its matrix from the frame it was drawn, which still agree, so its shadows stay put in the world;
   * only the edge of its coverage lags a frame. Everything is drawn while warming up and after a
   * camera jump (reset, restart, camera cut across the stage).
   */
  private turnCascades(camera: THREE.Camera): void {
    const lights = this.csm?.lights;
    if (!lights || lights.length < 3) return;
    const jump = camera.position.distanceToSquared(this.lastCameraPos) > CASCADE_JUMP_M * CASCADE_JUMP_M;
    this.lastCameraPos.copy(camera.position);
    const outer = lights.length - 1;
    this.cascadeTurn = (this.cascadeTurn + 1) % outer;
    for (let i = 1; i < lights.length; i++) {
      const shadow = lights[i].shadow;
      if (shadow) shadow.autoUpdate = this.warming || jump || i - 1 === this.cascadeTurn;
    }
  }
}

/** Camera movement in one frame (m) treated as a jump: every shadow cascade is redrawn. */
const CASCADE_JUMP_M = 15;

function insideNamed(o: THREE.Object3D, name: string): boolean {
  for (let p: THREE.Object3D | null = o; p; p = p.parent) if (p.name === name) return true;
  return false;
}

function colorize(d: MeshData, surfaceAt: (x: number, z: number) => SurfaceId): MeshData {
  if (d.colors) return d;
  const n = d.positions.length / 3;
  const colors = new Float32Array(n * 3);
  const c = new THREE.Color();
  for (let i = 0; i < n; i++) {
    c.setHex(SURFACE_COLORS[surfaceAt(d.positions[i * 3], d.positions[i * 3 + 2])] ?? 0x888888);
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }
  return { ...d, colors };
}

// ---- Stage furniture ----

const textCache = new Map<string, THREE.CanvasTexture>();
function textTexture(text: string, bg = '#0b0d10', fg = '#ff5a14'): THREE.CanvasTexture {
  const key = `${text}|${bg}|${fg}`;
  const hit = textCache.get(key);
  if (hit) return hit;
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 128;
  const g = c.getContext('2d')!;
  g.fillStyle = bg;
  g.fillRect(0, 0, 512, 128);
  g.fillStyle = fg;
  g.font = 'italic 800 84px "Barlow Condensed", "Arial Narrow", sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(text, 256, 68);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  textCache.set(key, t);
  return t;
}

/** Rendered cottage wall: two upper windows, a window and a door below, weathered render. */
let houseTex: THREE.CanvasTexture | null = null;
function houseTexture(): THREE.CanvasTexture {
  if (houseTex) return houseTex;
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 320;
  const g = c.getContext('2d')!;
  g.fillStyle = '#cbc3b1';
  g.fillRect(0, 0, 512, 320);
  let r = 7;
  for (let i = 0; i < 2600; i++) {
    r = (r * 16807) % 2147483647;
    const x = r % 512;
    r = (r * 16807) % 2147483647;
    const y = r % 320;
    g.fillStyle = `rgba(${90 + (r % 60)},${85 + (r % 50)},${70 + (r % 40)},0.12)`;
    g.fillRect(x, y, 3, 3);
  }
  // Damp staining near the ground.
  const grd = g.createLinearGradient(0, 250, 0, 320);
  grd.addColorStop(0, 'rgba(80,72,60,0)');
  grd.addColorStop(1, 'rgba(80,72,60,0.45)');
  g.fillStyle = grd;
  g.fillRect(0, 250, 512, 70);
  const win = (x: number, y: number, w: number, h: number) => {
    g.fillStyle = '#efeee8';
    g.fillRect(x - 6, y - 6, w + 12, h + 12);
    g.fillStyle = '#26313a';
    g.fillRect(x, y, w, h);
    g.fillStyle = '#efeee8';
    g.fillRect(x + w / 2 - 3, y, 6, h);
    g.fillRect(x, y + h / 2 - 3, w, 6);
  };
  win(70, 40, 90, 80);
  win(350, 40, 90, 80);
  win(70, 190, 90, 80);
  g.fillStyle = '#2f4a3a';
  g.fillRect(345, 175, 90, 145);
  g.fillStyle = '#8a8170';
  g.fillRect(335, 305, 110, 15);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  houseTex = t;
  return t;
}

const mats = {
  post: new THREE.MeshStandardNodeMaterial({ color: 0x1b1f24, roughness: 0.6, metalness: 0.4 }),
  bale: new THREE.MeshStandardNodeMaterial({ color: 0xc9a75a, roughness: 1 }),
  barrier: new THREE.MeshStandardNodeMaterial({ color: 0xd8d8d2, roughness: 0.8 }),
  wood: new THREE.MeshStandardNodeMaterial({ color: 0x6a4a30, roughness: 0.9 }),
  woodLight: new THREE.MeshStandardNodeMaterial({ color: 0x9a7650, roughness: 0.9 }),
  wall: new THREE.MeshStandardNodeMaterial({ color: 0xd9d2c3, roughness: 0.9 }),
  roof: new THREE.MeshStandardNodeMaterial({ color: 0x3e3f44, roughness: 0.75 }),
  concrete: new THREE.MeshStandardNodeMaterial({ color: 0x8c8a84, roughness: 0.85 }),
  tape: new THREE.MeshStandardNodeMaterial({ color: 0xd8321e, roughness: 0.6 }),
};

function propMesh(p: PropInstance): THREE.Object3D | null {
  const g = new THREE.Group();
  g.position.set(p.x, p.y, p.z);
  g.rotation.y = p.yaw;
  const box = (w: number, h: number, d: number, mat: THREE.Material, x = 0, y = 0, z = 0) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
    m.position.set(x, y, z);
    m.castShadow = true;
    m.receiveShadow = true;
    g.add(m);
    return m;
  };
  switch (p.type) {
    case 'arch': {
      const w = p.width ?? 10;
      box(0.3, 5, 0.3, mats.post, w / 2, 2.5, 0);
      box(0.3, 5, 0.3, mats.post, -w / 2, 2.5, 0);
      const banner = box(w + 0.4, 1.1, 0.2, new THREE.MeshStandardNodeMaterial({ map: textTexture(p.text ?? 'START'), roughness: 0.7 }), 0, 5, 0);
      banner.rotation.y = Math.PI;
      if (p.text === 'START') {
        // Countdown light gantry (lit by the game).
        const lights = new THREE.Group();
        lights.name = 'countdown-lights';
        for (let i = 0; i < 5; i++) {
          const l = new THREE.Mesh(new THREE.SphereGeometry(0.16, 12, 8), new THREE.MeshStandardNodeMaterial({ color: 0x220000, emissive: 0x000000, roughness: 0.3 }));
          l.position.set((i - 2) * 0.45, 4.2, -0.15);
          lights.add(l);
        }
        g.add(lights);
      }
      return g;
    }
    case 'board': {
      box(0.1, 2.0, 0.1, mats.post, 0, 1.0, 0);
      const panel = box(1.2, 0.9, 0.06, new THREE.MeshStandardNodeMaterial({ map: textTexture(p.text ?? '', '#ff5a14', '#0b0d10'), roughness: 0.6 }), 0, 2.0, 0);
      panel.rotation.y = Math.PI;
      return g;
    }
    case 'bale':
      box(1.2, 0.9, 1.25, mats.bale, 0, 0.45, 0);
      return g;
    case 'barrier':
      box(0.5, 0.9, 4, mats.barrier, 0, 0.45, 0);
      return g;
    case 'fence': {
      // One panel: a post at the start and two rails (the next panel's post closes it).
      const len = p.length ?? 6;
      box(0.12, 1.25, 0.12, mats.wood, 0, 0.5, -len / 2);
      box(0.06, 0.12, len, mats.woodLight, 0, 0.95, 0);
      box(0.06, 0.12, len, mats.woodLight, 0, 0.5, 0);
      return g;
    }
    case 'log': {
      const m = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.3, 5, 10), mats.wood);
      m.rotation.x = Math.PI / 2;
      m.position.y = 0.25;
      m.castShadow = true;
      g.add(m);
      return g;
    }
    case 'house': {
      const w = p.width ?? 7;
      const d = p.length ?? 8;
      box(w, 5, d, new THREE.MeshStandardNodeMaterial({ map: houseTexture(), roughness: 0.92 }), 0, 2.5, 0);
      box(0.7, 2.2, 0.7, mats.wall, w * 0.25, 7.2, d * 0.2);
      const roof = new THREE.Mesh(new THREE.ConeGeometry(Math.max(w, d) * 0.75, 3, 4), mats.roof);
      roof.position.y = 6.4;
      roof.receiveShadow = false;
      roof.rotation.y = Math.PI / 4;
      roof.scale.set(w / Math.max(w, d), 1, d / Math.max(w, d));
      roof.castShadow = true;
      g.add(roof);
      return g;
    }
    case 'bridge': {
      const len = p.length ?? 30;
      const w = p.width ?? 4.2;
      box(w + 0.4, 0.5, len, mats.concrete, 0, -0.27, 0);
      box(0.2, 0.9, len, mats.wood, w / 2 + 0.1, 0.45, 0);
      box(0.2, 0.9, len, mats.wood, -w / 2 - 0.1, 0.45, 0);
      box(1.2, 4, 1.6, mats.concrete, 0, -2.4, 0);
      return g;
    }
    case 'logpile': {
      // Timber stack lying along the road: 4-3-2 rows of logs.
      const len = p.length ?? 6;
      let k = 0;
      for (let row = 0; row < 3; row++) {
        const nInRow = 4 - row;
        for (let c = 0; c < nInRow; c++) {
          const r = 0.2 + 0.04 * (((k * 7) % 5) / 5);
          const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, len - 0.3 * ((k * 3) % 3), 9), k % 3 === 0 ? mats.woodLight : mats.wood);
          m.rotation.x = Math.PI / 2;
          m.position.set((c - (nInRow - 1) / 2) * 0.44, 0.22 + row * 0.38, (((k * 5) % 3) - 1) * 0.15);
          m.castShadow = true;
          m.receiveShadow = true;
          g.add(m);
          k++;
        }
      }
      return g;
    }
    case 'tape': {
      const len = p.length ?? 10;
      box(0.02, 0.08, len, mats.tape, 0, 0.85, 0);
      for (let z = -len / 2; z <= len / 2; z += 2.5) box(0.05, 1, 0.05, mats.wood, 0, 0.5, z);
      return g;
    }
    default:
      return null;
  }
}

/**
 * three r186's shadow pass renders every caster with one shared override material and copies each
 * caster's `alphaTest` onto it; Material's setter bumps the version whenever alphaTest crosses
 * zero, which rebuilds render objects mid-frame ("buffer used in submit while destroyed"). Give
 * opaque casters a negligible alpha test so the override never crosses zero. Call after adding
 * casters (scene build, car model).
 */
export function normalizeShadowCasters(root: THREE.Object3D): void {
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.castShadow) return;
    const list = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of list) if (m && m.alphaTest === 0) m.alphaTest = 0.001;
  });
}
